import { useId } from 'react';

import { CheckboxField } from '@nesso/ui/primitives/checkbox-field';
import { TreeNode } from '@nesso/ui/primitives/tree-node';

import type { TreatmentApproach } from '@/entities/session';
import { type InterventionsInput, treatmentApproachSchema } from '@/entities/session-note';
import { getTreatmentApproachLabel } from '@/entities/treatment-approach';
import { CheckboxListWithOther } from '@/shared/ui/checkbox-list/checkbox-list-with-other';
import { MultiSelect } from '@/shared/ui/common/multi-select';
import { FieldSection } from '@/shared/ui/forms/field-section';

type CheckboxOption = { value: string; label: string };
export type InterventionGroup = InterventionsInput[number];

const INTERVENTION_OPTIONS_BY_APPROACH: Partial<Record<string, CheckboxOption[]>> =
    Object.fromEntries(
        treatmentApproachSchema.options.map((variant) => [
            variant.shape.treatmentApproach.value,
            variant.shape.interventions
                .unwrap()
                .element.options.map((label) => ({ value: label, label })),
        ]),
    );

const GOAL_OPTIONS_BY_APPROACH: Partial<Record<string, CheckboxOption[]>> = Object.fromEntries(
    treatmentApproachSchema.options.map((variant) => [
        variant.shape.treatmentApproach.value,
        variant.shape.goals.unwrap().element.options.map((label) => ({ value: label, label })),
    ]),
);

type Props = {
    group: InterventionGroup;
    active: boolean;
    onUpdate: (update: Partial<InterventionGroup>) => void;
    onToggle: (active: boolean) => void;
};

export function NessoApproachItem({ group, active, onUpdate, onToggle }: Props) {
    const id = useId();
    const interventionOptions = INTERVENTION_OPTIONS_BY_APPROACH[group.treatmentApproach] ?? [];
    const goalOptions = GOAL_OPTIONS_BY_APPROACH[group.treatmentApproach] ?? [];
    const label = getTreatmentApproachLabel(group.treatmentApproach as TreatmentApproach);

    return (
        <TreeNode
            open={active}
            triggerAsToggle={false}
            contentClassName="gap-4"
            trigger={
                <CheckboxField
                    id={id}
                    label={label}
                    checked={active}
                    onCheckedChange={(value) => onToggle(value === true)}
                />
            }
        >
            <div className="flex-1 space-y-4 py-2">
                <FieldSection label="Interventions">
                    <MultiSelect
                        options={interventionOptions}
                        selected={group.interventions ?? []}
                        onChange={(interventions) =>
                            onUpdate({ interventions } as Partial<InterventionGroup>)
                        }
                        variant="mobile-dialog"
                        placeholder=""
                        groupLabel="Select all interventions used"
                        searchPlaceholder="Search interventions"
                        className="w-full min-h-15"
                    />
                </FieldSection>

                <FieldSection label="Intervention goals" className="gap-2">
                    <CheckboxListWithOther
                        columns={1}
                        options={goalOptions}
                        values={group.goals ?? []}
                        onValuesChange={(goals) =>
                            onUpdate({ goals } as Partial<InterventionGroup>)
                        }
                        otherValue={group.otherGoal}
                        onOtherValueChange={(otherGoal) =>
                            onUpdate({ otherGoal } as Partial<InterventionGroup>)
                        }
                    />
                </FieldSection>
            </div>
        </TreeNode>
    );
}
